import React from "react";
import PropTypes from "prop-types";
import { Modal, Tabs, Icon } from "antd";
import KycDocViewer from "./kyc-doc-viewer";
import { lookupApplicantHit } from "../trackwizz-utils";

const { TabPane } = Tabs;

function tabTitle(label, present) {
    return (
        <span className={"cc-kyc-tab"}>
            <Icon
                type={present ? "check-circle" : "exclamation-circle"}
                theme={present ? "filled" : "outlined"}
                className={
                    present ? "cc-kyc-tab--ok" : "cc-kyc-tab--missing"
                }
            />
            {label}
        </span>
    );
}

function DetailRow({ label, value }) {
    return (
        <div className={"cc-kyc-detail"}>
            <span className={"cc-kyc-detail__label"}>{label}</span>
            <div className={"cc-kyc-detail__value"}>{value || "—"}</div>
        </div>
    );
}

DetailRow.propTypes = { label: PropTypes.string, value: PropTypes.node };

/**
 * Full-screen-ish "Applicants KYC" viewer: left document pane (PAN / Aadhaar /
 * Photo & Contact tabs with zoom-rotate viewer), right read-only applicant
 * details, and an applicant pager ("1 out of N").
 */
class KycViewerModal extends React.Component {
    state = { tab: "photo", detailsCollapsed: false };

    toggleDetails = () =>
        this.setState(prev => ({ detailsCollapsed: !prev.detailsCollapsed }));

    componentDidUpdate(prevProps) {
        // When the modal (re)opens, honour the requested initial tab.
        if (
            this.props.visible &&
            !prevProps.visible &&
            this.props.initialTab &&
            this.props.initialTab !== this.state.tab
        ) {
            this.setState({ tab: this.props.initialTab });
        }
    }

    handleTab = tab => this.setState({ tab });

    step = delta => {
        const { index, applicants, onIndexChange } = this.props;
        const next = index + delta;
        if (next >= 0 && next < applicants.length) {
            onIndexChange(next);
        }
    };

    render() {
        const { visible, applicants, index, onClose, applicantHits } = this.props;
        const applicant = applicants[index];
        if (!applicant) {
            return null;
        }
        const docs = applicant.docsByTile;
        const v = applicant.verification;
        // Trackwizz per-applicant PDF — looked up by normalised name with a
        // positional fallback (see trackwizz-utils.lookupApplicantHit).
        const hit = lookupApplicantHit(applicantHits, applicant.name, index);
        const screeningPdfUrl = hit && hit.reportUrl;
        const hasScreeningPdf = Boolean(screeningPdfUrl);
        return (
            <Modal
                visible={visible}
                onCancel={onClose}
                footer={null}
                width={"98vw"}
                destroyOnClose
                className={"cc-modal cc-kyc-modal cc-kyc-modal--max"}
                title={
                    <div className={"cc-kyc-modal__title"}>
                        <span>Applicants KYC</span>
                        <span className={"cc-kyc-modal__header-actions"}>
                            <button
                                type={"button"}
                                className={"cc-kyc-modal__toggle"}
                                onClick={this.toggleDetails}
                                aria-label={
                                    this.state.detailsCollapsed
                                        ? "Show applicant details"
                                        : "Hide applicant details"
                                }
                            >
                                <Icon
                                    type={
                                        this.state.detailsCollapsed
                                            ? "menu-unfold"
                                            : "menu-fold"
                                    }
                                />
                                {this.state.detailsCollapsed
                                    ? "Show details"
                                    : "Hide details"}
                            </button>
                            <span className={"cc-kyc-modal__pager"}>
                                <button
                                    type={"button"}
                                    onClick={() => this.step(-1)}
                                    disabled={index === 0}
                                    aria-label={"Previous applicant"}
                                >
                                    <Icon type={"left"} />
                                </button>
                                <span className={"cc-kyc-modal__count"}>
                                    {index + 1} of {applicants.length}
                                </span>
                                <button
                                    type={"button"}
                                    onClick={() => this.step(1)}
                                    disabled={index === applicants.length - 1}
                                    aria-label={"Next applicant"}
                                >
                                    <Icon type={"right"} />
                                </button>
                            </span>
                        </span>
                    </div>
                }
            >
                <div
                    className={
                        "cc-kyc-modal__body" +
                        (this.state.detailsCollapsed
                            ? " cc-kyc-modal__body--nodetails"
                            : "")
                    }
                >
                    <div className={"cc-kyc-modal__docs"}>
                        <div className={"cc-kyc-modal__pane-title"}>Documents</div>
                        <Tabs
                            activeKey={this.state.tab}
                            onChange={this.handleTab}
                            animated={false}
                        >
                            <TabPane
                                tab={tabTitle("Photo", v.photo)}
                                key={"photo"}
                            >
                                <KycDocViewer
                                    filePath={docs.photo && docs.photo.file}
                                />
                            </TabPane>
                            <TabPane tab={tabTitle("PAN", v.pan)} key={"pan"}>
                                <KycDocViewer
                                    filePath={docs.pan && docs.pan.file}
                                />
                            </TabPane>
                            <TabPane
                                tab={tabTitle("AADHAR", v.aadhaar)}
                                key={"aadhaar"}
                            >
                                <KycDocViewer
                                    filePath={docs.aadhaar && docs.aadhaar.file}
                                />
                            </TabPane>
                            <TabPane
                                tab={tabTitle(
                                    "Screening",
                                    hasScreeningPdf
                                )}
                                key={"screening"}
                            >
                                {hit &&
                                (hit.probable || hit.confirmed) ? (
                                    <div className={"cc-kyc-cibilremark"}>
                                        <span
                                            className={
                                                "cc-kyc-cibilremark__label"
                                            }
                                        >
                                            <Icon type={"safety-certificate"} />
                                            AML / PEP / sanctions hits
                                        </span>
                                        <div
                                            className={"cc-kyc-cibilremark__text"}
                                        >
                                            <div>
                                                <b>Probable Hit:</b>{" "}
                                                {hit.probable || "No Hit"}
                                            </div>
                                            <div>
                                                <b>Confirmed Hit:</b>{" "}
                                                {hit.confirmed || "No Hit"}
                                            </div>
                                        </div>
                                    </div>
                                ) : null}
                                <KycDocViewer filePath={screeningPdfUrl} />
                            </TabPane>
                            <TabPane
                                tab={tabTitle(
                                    "CIBIL",
                                    Boolean(
                                        applicant.bureau &&
                                            applicant.bureau.report_url
                                    )
                                )}
                                key={"cibil"}
                            >
                                {applicant.bureau &&
                                applicant.bureau.cibil_remarks ? (
                                    <div className={"cc-kyc-cibilremark"}>
                                        <span
                                            className={
                                                "cc-kyc-cibilremark__label"
                                            }
                                        >
                                            <Icon type={"highlight"} />
                                            Bureau remarks
                                        </span>
                                        <div
                                            className={"cc-kyc-cibilremark__text"}
                                        >
                                            {applicant.bureau.cibil_remarks}
                                        </div>
                                    </div>
                                ) : null}
                                <KycDocViewer
                                    filePath={
                                        applicant.bureau &&
                                        applicant.bureau.report_url
                                    }
                                />
                            </TabPane>
                        </Tabs>
                    </div>
                    <div className={"cc-kyc-modal__details"}>
                        <div className={"cc-kyc-modal__pane-title"}>
                            Applicant Details
                        </div>
                        <div className={"cc-kyc-modal__tags"}>
                            <span
                                className={
                                    "cc-kyc-roletag" +
                                    (applicant.isMainBorrower
                                        ? " cc-kyc-roletag--main"
                                        : "")
                                }
                            >
                                {applicant.isMainBorrower
                                    ? "Main Borrower"
                                    : applicant.type || "Co-applicant"}
                            </span>
                            {applicant.bureau && applicant.bureau.cibil_score ? (
                                <span
                                    className={
                                        "cc-kyc-cibiltag cc-kyc-cibiltag--" +
                                        ((applicant.bureau.band &&
                                            applicant.bureau.band.tone) ||
                                            "muted")
                                    }
                                >
                                    CIBIL {applicant.bureau.cibil_score}
                                </span>
                            ) : null}
                        </div>
                        <DetailRow label={"Full name"} value={applicant.name} />
                        <DetailRow
                            label={"Father name"}
                            value={applicant.fatherName}
                        />
                        <DetailRow
                            label={"Date of birth"}
                            value={applicant.dobLabel}
                        />
                        <DetailRow label={"PAN"} value={applicant.pan} />
                        <DetailRow
                            label={"Mobile number"}
                            value={applicant.phone}
                        />
                        <DetailRow label={"Email"} value={applicant.email} />
                        <DetailRow
                            label={"Residence address"}
                            value={applicant.address}
                        />
                    </div>
                </div>
            </Modal>
        );
    }
}

KycViewerModal.propTypes = {
    visible: PropTypes.bool,
    applicants: PropTypes.array.isRequired,
    index: PropTypes.number.isRequired,
    initialTab: PropTypes.string,
    // Map keyed by normalised applicant name (see trackwizz-utils) — feeds the
    // Screening tab.
    applicantHits: PropTypes.object,
    onIndexChange: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired
};

export default KycViewerModal;
